import React, { useState } from "react";
import { toast } from "sonner";
import { authChangePassword } from "@/lib/api";

const TABS = ["Account", "Workspace", "Review workflow", "Roles & SLA", "Evidence", "Publication targets", "Notifications"];

export default function Settings() {
  const [tab, setTab] = useState("Account");
  return (
    <div className="p-8 md:p-10 space-y-6" data-testid="settings-page">
      <div>
        <div className="tracked-caps">Administration</div>
        <h2 className="mt-1 font-serif-display text-3xl">Settings</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Workspace-level configuration for review workflow, roles, retention, and publication targets.
        </p>
      </div>

      <div className="grid md:grid-cols-12 gap-5">
        <aside className="md:col-span-3">
          <ul className="space-y-1">
            {TABS.map((t) => (
              <li key={t}>
                <button
                  onClick={() => setTab(t)}
                  data-testid={`settings-tab-${t}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm border ${tab === t ? "bg-brand text-brand-foreground border-brand" : "bg-card border-border hover:bg-muted"}`}
                >
                  {t}
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <section className="md:col-span-9 rounded-lg border border-border bg-card p-6 space-y-6">
          {tab === "Account" && <Account />}
          {tab === "Workspace" && <Workspace />}
          {tab === "Review workflow" && <ReviewWorkflow />}
          {tab === "Roles & SLA" && <RolesSla />}
          {tab === "Evidence" && <Evidence />}
          {tab === "Publication targets" && <PubTargets />}
          {tab === "Notifications" && <Notifications />}
        </section>
      </div>
    </div>
  );
}

const Row = ({ k, v, help }) => (
  <div className="py-4 grid md:grid-cols-3 gap-4 hairline-b last:border-0">
    <div>
      <div className="text-sm font-medium">{k}</div>
      {help && <div className="text-xs text-muted-foreground mt-1">{help}</div>}
    </div>
    <div className="md:col-span-2">{v}</div>
  </div>
);

function Input({ defaultValue, placeholder }) {
  return <input defaultValue={defaultValue} placeholder={placeholder} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand" />;
}

function Toggle({ on = true, label }) {
  return <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked={on} className="h-4 w-4 rounded border-border" />{label}</label>;
}

function Workspace() {
  return (
    <div>
      <h3 className="font-serif-display text-xl">Workspace profile</h3>
      <div className="mt-4 divide-y divide-border">
        <Row k="Workspace name" v={<Input defaultValue="Acme Fintech · Production" />} />
        <Row k="Region" v={<Input defaultValue="EU (Frankfurt)" />} />
        <Row k="Primary domain" v={<Input defaultValue="EU Payments" />} />
      </div>
    </div>
  );
}

function ReviewWorkflow() {
  return (
    <div>
      <h3 className="font-serif-display text-xl">Review workflow</h3>
      <div className="mt-4 divide-y divide-border">
        <Row k="Two-person review" help="Require a second reviewer on policy-flagged artifacts." v={<Toggle on label="Enabled for policy flags" />} />
        <Row k="Auto-escalate after" help="Escalate candidates still in review after this duration." v={<Input defaultValue="48 hours" />} />
        <Row k="Publication gate" v={<Toggle on label="Block publication without complete evidence record" />} />
      </div>
    </div>
  );
}

function RolesSla() {
  return (
    <div>
      <h3 className="font-serif-display text-xl">Roles & SLA</h3>
      <div className="mt-4 divide-y divide-border">
        <Row k="Reviewer roles" v={<div className="flex flex-wrap gap-2">{["Ops Lead", "Knowledge Manager", "Compliance Reviewer"].map((r) => <span key={r} className="rounded border border-border bg-muted/40 px-2 py-0.5 text-xs">{r}</span>)}</div>} />
        <Row k="SLA · EU Payments" v={<Input defaultValue="48 hours" />} />
        <Row k="SLA · Global Refunds" v={<Input defaultValue="48 hours" />} />
      </div>
    </div>
  );
}

function Evidence() {
  return (
    <div>
      <h3 className="font-serif-display text-xl">Evidence retention</h3>
      <div className="mt-4 divide-y divide-border">
        <Row k="Retention window" v={<Input defaultValue="7 years" />} />
        <Row k="Immutable log" v={<Toggle on label="Write-once evidence ledger" />} />
        <Row k="Export signing" v={<Toggle on label="Sign PDF exports with workspace key" />} />
      </div>
    </div>
  );
}

function PubTargets() {
  return (
    <div>
      <h3 className="font-serif-display text-xl">Publication targets</h3>
      <ul className="mt-4 divide-y divide-border">
        {["Support Playbook / EU Payments", "Support Playbook / Refunds", "Enablement / Tier 2", "Help Center / Troubleshooting"].map((t) => (
          <li key={t} className="py-3 flex items-center justify-between text-sm">
            <span>{t}</span>
            <button className="text-xs text-muted-foreground hover:text-foreground">Configure</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Notifications() {
  return (
    <div>
      <h3 className="font-serif-display text-xl">Notifications</h3>
      <div className="mt-4 divide-y divide-border">
        <Row k="Daily queue digest" v={<Toggle on label="09:00 local time" />} />
        <Row k="Policy flag alerts" v={<Toggle on label="Immediate — email + Slack" />} />
        <Row k="Revocation alerts" v={<Toggle on label="Immediate — email + Slack" />} />
      </div>
    </div>
  );
}

function Account() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await authChangePassword(current, next);
      toast.success("Password updated successfully.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password.");
    } finally {
      setSaving(false);
    }
  }

  const fieldClass = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand";

  return (
    <div>
      <h3 className="font-serif-display text-xl mb-1">Account</h3>
      <p className="text-sm text-muted-foreground mb-6">Manage your password and personal settings.</p>
      <form onSubmit={handleSubmit} className="max-w-sm space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1.5">Current password</label>
          <input type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={fieldClass} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">New password</label>
          <input type="password" autoComplete="new-password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className={fieldClass} placeholder="Min. 8 characters" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">Confirm new password</label>
          <input type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={fieldClass} />
        </div>
        <button type="submit" disabled={saving} className="rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
          {saving ? "Saving…" : "Update password"}
        </button>
      </form>
    </div>
  );
}
